import PropTypes from 'prop-types';
import React from 'react';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Button,
} from '@unite-us/app-components';
import { phones as phonesUtils } from '@unite-us/client-utils';
import { compact, wget } from 'lodash';
import { epochToDate } from 'common/utils/utils';
import Address from 'common/display/Address';
import PhoneNumber from 'common/display/PhoneNumberDisplay';
import SimpleList from 'common/display/SimpleList';
import { getAdditionalInfoDisplay } from 'common/display/Profile/CommunicationAdditionalInfo/utils';
import { connect } from 'react-redux';

const capitalizeWords = (str) => {
  if (!str) return '';
  return str.split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getMatchStatusPhrase = (hasCriteria, matchValue) => {
  if (!hasCriteria || matchValue === undefined) {
    return null;
  }
  return matchValue ? 'Matches your search criteria' : 'Does not match your search criteria';
};

const hasAddressDetails = (address = {}) => {
  if (!address) {
    return false;
  }
  return [
    address.line_1,
    address.city,
    address.state,
    address.postal_code,
  ].some(Boolean);
};

const buildCommunicationDetails = ({ contactCommunicationTypesEnums, phone }) => (
  getAdditionalInfoDisplay({
    communicationTypes: phone.acceptable_communication_types,
    enums: contactCommunicationTypesEnums,
    isPrimary: phone.is_primary,
  })
);

const formatPhoneForA11y = (phone = {}, contactCommunicationTypesEnums = []) => {
  if (!phone || !phone.phone_number) {
    return null;
  }

  const formattedNumber = phonesUtils.formatPhoneNumber(phone.phone_number);
  const header = compact([
    phone.phone_type,
    buildCommunicationDetails({ contactCommunicationTypesEnums, phone }),
  ]).join(' ');

  const extensionText = phone.extension ? `extension ${phone.extension}` : '';
  const numberWithExtension = compact([formattedNumber, extensionText]).join(' ');

  return compact([header, numberWithExtension]).join(' ');
};

const buildPhoneAccessibleLabel = (phones = [], contactCommunicationTypesEnums = []) => {
  const formattedPhones = (phones || [])
    .map((phone) => formatPhoneForA11y(phone, contactCommunicationTypesEnums))
    .filter(Boolean);

  if (!formattedPhones.length) {
    return 'Phone information';
  }

  return `Phone ${formattedPhones.join('; ')}`;
};

const formatAddressForA11y = (address = {}) => {
  if (!hasAddressDetails(address)) {
    return null;
  }

  const tags = compact([
    address.address_type,
    address.is_primary ? 'primary' : null,
  ]).join(' ');

  const cityState = compact([address.city, address.state]).join(', ');
  const locationLine = compact([cityState, address.postal_code]).join(' ');

  const body = compact([
    address.line_1,
    address.line_2,
    locationLine,
    address.county ? `${address.county} county` : null,
  ]).join(', ');

  return compact([tags, body]).join(', ');
};

const buildAddressAccessibleLabel = (addresses = []) => {
  const formattedAddresses = (addresses || [])
    .map(formatAddressForA11y)
    .filter(Boolean);

  if (!formattedAddresses.length) {
    return 'Address information';
  }

  return `Address ${formattedAddresses.join('; ')}`;
};

const PhoneNumberText = ({ item, shortDisplay }) => (
  <PhoneNumber
    item={item}
    shortDisplay={shortDisplay}
    noLink
    typeDisplayInline
  />
);

PhoneNumberText.propTypes = {
  item: PropTypes.object.isRequired,
  shortDisplay: PropTypes.bool,
};

const ACTIVATION_KEYS = new Set(['Enter', ' ']);

const preventStaticLinkDefault = (event) => {
  if (event.type === 'click') {
    event.preventDefault();
    return;
  }

  if (ACTIVATION_KEYS.has(event.key)) {
    event.preventDefault();
  }
};

const ContactCard = ({
  id,
  contact,
  onSelectContact,
  indexNumber,
  matchDetails,
  contactCommunicationTypesEnums,
}) => {
  const {
    hasCriteria = false,
    overall: overallMatch,
    fields: {
      dateOfBirth: dateOfBirthMatch,
    } = {},
  } = matchDetails || {};

  const fallbackName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  const displayName = capitalizeWords(contact.full_name || fallbackName || 'Client');
  const headerText = indexNumber !== undefined ? `${indexNumber + 1}: ${displayName}` : displayName;

  const overallMatchPhrase = getMatchStatusPhrase(hasCriteria, overallMatch);
  const cardLabelParts = [];
  const recordPositionPrefix = indexNumber !== undefined ? `${indexNumber + 1}. ` : '';
  cardLabelParts.push(`${recordPositionPrefix}Existing record ${displayName}`.trim());
  if (overallMatchPhrase) {
    cardLabelParts.push(overallMatchPhrase);
  }
  const cardAriaLabel = cardLabelParts.join(', ');

  const dateOfBirthValue = epochToDate(contact.date_of_birth);
  const dateOfBirthPhrase = getMatchStatusPhrase(hasCriteria, dateOfBirthMatch);
  const dateOfBirthLabelPieces = [`Date of Birth ${dateOfBirthValue}`.trim()];
  if (dateOfBirthPhrase) {
    dateOfBirthLabelPieces.push(dateOfBirthPhrase);
  }
  const dateOfBirthAriaLabel = dateOfBirthLabelPieces.join(', ');
  const phoneAccessibleLabel = buildPhoneAccessibleLabel(contact.phone_numbers, contactCommunicationTypesEnums);
  const addressAccessibleLabel = buildAddressAccessibleLabel(contact.addresses);
  const summaryLinkId = `${id}-summary-link`;
  const dateLinkId = `${id}-date-link`;
  const phoneLinkId = `${id}-phone-link`;
  const addressLinkId = `${id}-address-link`;
  const phoneLabelId = `${id}-phone-label`;
  const phoneValueId = `${id}-phone-value`;
  const addressLabelId = `${id}-address-label`;
  const addressValueId = `${id}-address-value`;

  return (
    <li className="contact-card focus-outline" role="article" aria-label={cardAriaLabel}>
      <BaseCard>
        <BaseCardHeader
          title={(
            <a
              id={summaryLinkId}
              href="#"
              className="contact-card__focus-link contact-card__focus-link--name"
              onClick={preventStaticLinkDefault}
              onKeyDown={preventStaticLinkDefault}
              aria-label={cardAriaLabel}
            >
              <b aria-hidden="true">{headerText}</b>
            </a>
          )}
        />
        <BaseCardBody withPadding>
          <a
            id={dateLinkId}
            href="#"
            className="contact-card__focus-link block no-underline outline-none"
            onClick={preventStaticLinkDefault}
            onKeyDown={preventStaticLinkDefault}
            aria-label={dateOfBirthAriaLabel}
          >
            <dl className="contact-card__detail mb-4" aria-hidden="true">
              <dt className="font-regular-font text-text-blue">Date of Birth</dt>
              <dd className="font-regular-font text-text-blue">{dateOfBirthValue}</dd>
            </dl>
          </a>

          <a
            id={phoneLinkId}
            href="#"
            className="contact-card__focus-link block no-underline outline-none"
            onClick={preventStaticLinkDefault}
            onKeyDown={preventStaticLinkDefault}
            aria-label={phoneAccessibleLabel}
          >
            <dl className="contact-card__detail mb-4" aria-hidden="true">
              <dt id={phoneLabelId} className="font-regular-font text-text-blue">Phone</dt>
              <dd id={phoneValueId} className="font-regular-font text-text-blue">
                <SimpleList list={contact.phone_numbers} ListElement={PhoneNumberText} />
              </dd>
            </dl>
          </a>

          <a
            id={addressLinkId}
            href="#"
            className="contact-card__focus-link block no-underline outline-none"
            onClick={preventStaticLinkDefault}
            onKeyDown={preventStaticLinkDefault}
            aria-label={addressAccessibleLabel}
          >
            <dl className="contact-card__detail mb-4" aria-hidden="true">
              <dt id={addressLabelId} className="font-regular-font text-text-blue">Address</dt>
              <dd id={addressValueId} className="font-regular-font text-text-blue">
                <SimpleList list={contact.addresses} ListElement={Address} shortDisplay />
              </dd>
            </dl>
          </a>

          <Button
            id={`${id}-use-btn`}
            className="contact-card__confirmation"
            label="Use This Record"
            onClick={onSelectContact(contact)}
            primary
            ariaExpanded={null}
          />
        </BaseCardBody>
      </BaseCard>
    </li>
  );
};

ContactCard.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    date_of_birth: PropTypes.number,
    phone_numbers: PropTypes.array,
    addresses: PropTypes.array,
  }).isRequired,
  id: PropTypes.string.isRequired,
  onSelectContact: PropTypes.func.isRequired,
  indexNumber: PropTypes.number,
  matchDetails: PropTypes.shape({
    hasCriteria: PropTypes.bool,
    overall: PropTypes.bool,
    fields: PropTypes.shape({
      dateOfBirth: PropTypes.bool,
      firstName: PropTypes.bool,
      lastName: PropTypes.bool,
    }),
  }),
  contactCommunicationTypesEnums: PropTypes.array,
};

ContactCard.defaultProps = {
  indexNumber: undefined,
  matchDetails: null,
  contactCommunicationTypesEnums: [],
};

function mapStateToProps(state) {
  return {
    contactCommunicationTypesEnums: wget(
      state,
      'session.enums.contact_data.communication_types',
      [],
    ),
  };
}

export default connect(mapStateToProps)(ContactCard);
